import localforage from 'localforage'

/**
 * Auth storage utilities that always use a fresh IndexedDB connection.
 *
 * This prevents stale connection issues that can occur when a browser tab
 * is backgrounded for an extended period. By creating a fresh localforage
 * instance for each operation, we ensure we always read/write the latest data.
 *
 * This is used for refresh token operations which are rare (every few hours),
 * so the small overhead of creating a new instance is negligible.
 */

const DB_NAME = 'rpp'
const REFRESH_TOKEN_KEY = 'refreshToken'

/**
 * Creates a fresh localforage instance to ensure we get a new IndexedDB connection.
 * This prevents issues with stale connections after tab backgrounding.
 */
function createFreshStore() {
  return localforage.createInstance({ name: DB_NAME })
}

/**
 * Gets the refresh token using a fresh IndexedDB connection.
 * Always creates a new connection to avoid stale reads.
 */
export async function getRefreshToken(): Promise<string | null> {
  const store = createFreshStore()
  return store.getItem<string>(REFRESH_TOKEN_KEY)
}

/**
 * Sets the refresh token using a fresh IndexedDB connection.
 * Always creates a new connection to ensure the write is committed.
 */
export async function setRefreshToken(token: string): Promise<void> {
  const store = createFreshStore()
  await store.setItem(REFRESH_TOKEN_KEY, token)
}

/**
 * Removes the refresh token using a fresh IndexedDB connection.
 */
export async function removeRefreshToken(): Promise<void> {
  const store = createFreshStore()
  await store.removeItem(REFRESH_TOKEN_KEY)
}
